import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { MIN_COLUMN_PERCENT } from "./documentEditor";
import { rectEquals, type OverlayRect } from "./overlayGeometry";
import {
  EDITOR_CHROME_ATTRIBUTE,
  findRenderedBlockInstances,
  INSTANCE_OF_ATTRIBUTE,
  isRenderArtifact,
  visiblePartRect,
} from "./renderedDom";

/** How far either side of a boundary the grip takes the pointer, in px. */
const GRIP_HALF_WIDTH_PX = 4;

/** What a boundary's drag commits to: a table column, or an fp-columns pair. */
type BoundaryTarget =
  | { kind: "table"; nodeId: string; neighbourNodeId: string }
  | { kind: "columns"; nodeId: string; index: number };

/** One draggable column boundary: the pair it divides, and where it sits. */
interface ColumnBoundary {
  /** Stable render key for the grip. */
  key: string;
  target: BoundaryTarget;
  rect: OverlayRect;
}

/** A resize drag in flight, holding what the pointer delta is measured against. */
interface ResizeDrag {
  target: BoundaryTarget;
  startX: number;
  /** The pair's rendered width, which the drag divides between the two. */
  pairPx: number;
  /** The resized column's rendered width when the drag started. */
  startPx: number;
  /** The pair's authored width, which the drag divides between the two. */
  pairPercent: number;
  /** The width the drag has reached, committed on release. */
  percent: number;
  /** True once the pointer has actually moved, so a bare click commits nothing. */
  moved: boolean;
}

interface ColumnResizeHandlesProps {
  /** Ref to the scrollable preview container the grips are positioned in. */
  containerRef: RefObject<HTMLDivElement>;
  /** The selected table's node id, or null when no table is selected. */
  tableNodeId: string | null;
  /** The selection's enclosing fp-columns row's node id, or null. */
  columnsNodeId: string | null;
  /** Commits a table column's new percentage width. */
  onResize: (nodeId: string, percent: number) => void;
  /** Commits an fp-columns column's new percentage width by its position. */
  onResizeColumns: (nodeId: string, index: number, percent: number) => void;
}

/**
 * Drag grips on the selected table's or fp-columns row's column boundaries. A
 * drag trades width between the two columns it divides and leaves the rest of
 * the row alone, which is what lets a percentage row be re-proportioned without
 * the author doing the arithmetic. The layout follows the pointer by having its
 * rendered cell widths written directly - no reparse, the way the colour knobs
 * preview - and one document edit lands on release, so a drag is one undo step.
 * Grips track the rendered DOM with a requestAnimationFrame loop, so they
 * follow scrolling, re-renders and async pagination with no extra wiring.
 * @param containerRef - The scrollable preview container
 * @param tableNodeId - The selected table's node id
 * @param columnsNodeId - The selection's enclosing fp-columns row's node id
 * @param onResize - Commits a table column's new percentage width
 * @param onResizeColumns - Commits an fp-columns column's new percentage width
 * @returns The column resize grips
 */
export function ColumnResizeHandles({
  containerRef,
  tableNodeId,
  columnsNodeId,
  onResize,
  onResizeColumns,
}: ColumnResizeHandlesProps) {
  const [boundaries, setBoundaries] = useState<ColumnBoundary[]>([]);
  const dragRef = useRef<ResizeDrag | null>(null);

  useEffect(() => {
    if (tableNodeId === null && columnsNodeId === null) {
      setBoundaries([]);
      return;
    }
    let frame = 0;
    const track = () => {
      const container = containerRef.current;
      const next = container
        ? [
            ...(tableNodeId === null
              ? []
              : resolveTableBoundaries(container, tableNodeId)),
            ...(columnsNodeId === null
              ? []
              : resolveColumnsBoundaries(container, columnsNodeId)),
          ]
        : [];
      setBoundaries((previous) =>
        boundariesEqual(previous, next) ? previous : next,
      );
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);
    return () => cancelAnimationFrame(frame);
  }, [containerRef, tableNodeId, columnsNodeId]);

  const beginResize = (
    event: ReactPointerEvent<HTMLDivElement>,
    boundary: ColumnBoundary,
  ) => {
    const container = containerRef.current;
    if (!container || event.button !== 0) {
      return;
    }
    const { cells, neighbourCells } = boundaryCells(container, boundary.target);
    const percent = percentageWidthOf(cells[0], boundary.target.kind);
    const neighbourPercent = percentageWidthOf(
      neighbourCells[0],
      boundary.target.kind,
    );
    if (percent === null || neighbourPercent === null) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const startPx = cells[0]?.getBoundingClientRect().width ?? 0;
    dragRef.current = {
      target: boundary.target,
      startX: event.clientX,
      startPx,
      pairPx: startPx + (neighbourCells[0]?.getBoundingClientRect().width ?? 0),
      pairPercent: percent + neighbourPercent,
      percent,
      moved: false,
    };
  };

  const updateResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const container = containerRef.current;
    if (!drag || !container || drag.pairPx <= 0) {
      return;
    }
    const minPx = (MIN_COLUMN_PERCENT / drag.pairPercent) * drag.pairPx;
    const nextPx = Math.min(
      Math.max(drag.startPx + (event.clientX - drag.startX), minPx),
      drag.pairPx - minPx,
    );
    drag.percent = (nextPx / drag.pairPx) * drag.pairPercent;
    drag.moved = true;
    // Cells are re-resolved on every move rather than held from the drag's
    // start: painting reflows the content, the reflow can repaginate the
    // document, and repagination replaces rendered copies - a cached list
    // would paint detached nodes and leave the fresh copies at their old size.
    const { cells, neighbourCells } = boundaryCells(container, drag.target);
    paintWidth(cells, drag.percent, drag.target.kind);
    paintWidth(
      neighbourCells,
      drag.pairPercent - drag.percent,
      drag.target.kind,
    );
  };

  const finishResize = () => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    dragRef.current = null;
    if (!drag.moved) {
      return;
    }
    if (drag.target.kind === "table") {
      onResize(drag.target.nodeId, drag.percent);
      return;
    }
    onResizeColumns(drag.target.nodeId, drag.target.index, drag.percent);
  };

  return (
    <>
      {boundaries.map((boundary) => (
        <div
          key={boundary.key}
          {...{ [EDITOR_CHROME_ATTRIBUTE]: "true" }}
          className="group absolute z-40 flex cursor-col-resize justify-center"
          style={{
            top: boundary.rect.top,
            left: boundary.rect.left,
            width: boundary.rect.width,
            height: boundary.rect.height,
          }}
          onPointerDown={(event) => beginResize(event, boundary)}
          onPointerMove={updateResize}
          onPointerUp={finishResize}
          onPointerCancel={finishResize}
        >
          <div className="h-full w-px bg-blue-500/30 transition-colors group-hover:w-[2px] group-hover:bg-blue-500" />
        </div>
      ))}
    </>
  );
}

/**
 * Resolves the grips for a table: one per boundary between two adjacent
 * percentage-sized columns, on every visible rendered copy of the table. A
 * table sliced across pages renders in full once per page, each copy clipped
 * by its page's viewport, so each copy's grips span only its visible part -
 * never past the page edge. A boundary renders one grip per vertical stretch
 * where it actually exists, so no line is drawn through the middle of a merged
 * cell spanning across it. A pair that is not percentage-sized gets none,
 * since the table sizes those itself and there is no share to trade. The
 * boundaries anchor to the header row, or - when no column declares a header
 * and the table renders none - to the first body row made of the bound
 * columns, whose cells carry the same column node ids.
 * @param container - The scrollable preview container
 * @param tableNodeId - The selected table's node id
 * @returns The boundaries, empty when the table declares none it can divide
 */
function resolveTableBoundaries(
  container: HTMLElement,
  tableNodeId: string,
): ColumnBoundary[] {
  const containerRect = container.getBoundingClientRect();
  return findRenderedBlockInstances(container, tableNodeId).flatMap(
    (table, instanceIndex) => {
      const anchorRow = table.querySelector("thead > tr") ?? mainBodyRow(table);
      const visible =
        anchorRow === null ? null : visiblePartRect(container, table);
      if (anchorRow === null || visible === null) {
        return [];
      }
      const anchors = Array.from(anchorRow.children).filter(
        (cell): cell is HTMLElement => cell instanceof HTMLElement,
      );
      return anchors.flatMap((cell, index) => {
        const neighbour = anchors[index + 1];
        const nodeId = cell.getAttribute("data-fp-node");
        const neighbourNodeId = neighbour?.getAttribute("data-fp-node") ?? null;
        if (
          nodeId === null ||
          neighbourNodeId === null ||
          percentageWidthOf(cell, "table") === null ||
          percentageWidthOf(neighbour, "table") === null
        ) {
          return [];
        }
        const rect = cell.getBoundingClientRect();
        return boundarySegments(table, rect.right, visible).map(
          (segment, segmentIndex) => ({
            key: `${nodeId}:${instanceIndex}:${segmentIndex}`,
            target: { kind: "table" as const, nodeId, neighbourNodeId },
            rect: {
              top: segment.top - containerRect.top + container.scrollTop,
              left:
                rect.right -
                containerRect.left +
                container.scrollLeft -
                GRIP_HALF_WIDTH_PX,
              width: GRIP_HALF_WIDTH_PX * 2,
              height: segment.bottom - segment.top,
            },
          }),
        );
      });
    },
  );
}

/** A vertical stretch of a column boundary, in viewport coordinates. */
interface BoundarySegment {
  top: number;
  bottom: number;
}

/**
 * The vertical stretches of one column boundary that actually exist: the
 * boundary's visible span minus every rendered cell that straddles it - a
 * merged cell spanning across the boundary has no edge there, so a grip drawn
 * through it would paint a line down the middle of the cell.
 * @param table - The rendered table copy
 * @param x - The boundary's viewport X
 * @param visible - The table's visible part, in viewport coordinates
 * @returns The boundary's segments, top to bottom
 */
function boundarySegments(
  table: HTMLElement,
  x: number,
  visible: BoundarySegment,
): BoundarySegment[] {
  const blocked = Array.from(table.querySelectorAll("td, th"))
    .map((cell) => cell.getBoundingClientRect())
    .filter((rect) => rect.left < x - 1 && rect.right > x + 1)
    .map((rect) => ({ top: rect.top, bottom: rect.bottom }))
    .sort((a, b) => a.top - b.top);
  return blocked.reduce<BoundarySegment[]>(
    (segments, block) => {
      const last = segments.at(-1);
      if (!last || block.bottom <= last.top || block.top >= last.bottom) {
        return segments;
      }
      return [
        ...segments.slice(0, -1),
        ...(block.top > last.top ? [{ top: last.top, bottom: block.top }] : []),
        ...(block.bottom < last.bottom
          ? [{ top: block.bottom, bottom: last.bottom }]
          : []),
      ];
    },
    [{ top: visible.top, bottom: visible.bottom }],
  );
}

/**
 * The first body row holding one cell per grid column, which anchors the grips
 * of a table rendering no header row. Only such a row's cells name a column of
 * their own - section headings, notes and spacers span or omit columns, and a
 * drag committed against one of those would move the wrong column. The cell ids
 * are `<fp-column>` ids in a line-bound table and `<fp-cell>` ids in a static
 * one, both of which the commit resolves to the same ruler pair.
 * @param table - A rendered copy of the table
 * @returns The row, or null when the table renders no such row
 */
function mainBodyRow(table: HTMLElement): Element | null {
  const columnCount = table.querySelectorAll(":scope > colgroup > col").length;
  return (
    Array.from(table.querySelectorAll(":scope > tbody > tr")).find(
      (row) =>
        row.children.length === columnCount &&
        Array.from(row.children).every((cell) =>
          cell.hasAttribute("data-fp-node"),
        ),
    ) ?? null
  );
}

/**
 * Resolves the grips for an fp-columns row: one per boundary between two
 * adjacent percentage-sized columns, centred in the gap between them, on every
 * visible rendered copy of the row and spanning only each copy's visible part,
 * so a row clipped by its page's viewport never draws grips past the page
 * edge. The columns are addressed by position, since only a column standing in
 * for an `<fp-cell>` carries a node id of its own.
 * @param container - The scrollable preview container
 * @param columnsNodeId - The fp-columns row's node id
 * @returns The boundaries, empty when the row declares none it can divide
 */
function resolveColumnsBoundaries(
  container: HTMLElement,
  columnsNodeId: string,
): ColumnBoundary[] {
  const containerRect = container.getBoundingClientRect();
  return findRenderedBlockInstances(container, columnsNodeId).flatMap(
    (row, instanceIndex) => {
      const visible = visiblePartRect(container, row);
      if (visible === null) {
        return [];
      }
      const cells = Array.from(row.children).filter(
        (cell): cell is HTMLElement => cell instanceof HTMLElement,
      );
      return cells.flatMap((cell, index) => {
        const neighbour = cells[index + 1];
        if (
          neighbour === undefined ||
          percentageWidthOf(cell, "columns") === null ||
          percentageWidthOf(neighbour, "columns") === null
        ) {
          return [];
        }
        // The grip owns the whole gap plus a reach past both borders, so
        // grabbing right on a column's edge - where the selection outline
        // draws - works.
        const gapStart = cell.getBoundingClientRect().right;
        const gapEnd = neighbour.getBoundingClientRect().left;
        return [
          {
            key: `${columnsNodeId}:${index}:${instanceIndex}`,
            target: { kind: "columns" as const, nodeId: columnsNodeId, index },
            rect: {
              top: visible.top - containerRect.top + container.scrollTop,
              left:
                gapStart -
                containerRect.left +
                container.scrollLeft -
                GRIP_HALF_WIDTH_PX,
              width: gapEnd - gapStart + GRIP_HALF_WIDTH_PX * 2,
              height: visible.bottom - visible.top,
            },
          },
        ];
      });
    },
  );
}

/**
 * Compares two boundary lists, so the tracking loop only re-renders on a move.
 * @param a - The previous boundaries
 * @param b - The next boundaries
 * @returns True when both describe the same grips in the same places
 */
function boundariesEqual(a: ColumnBoundary[], b: ColumnBoundary[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (boundary, index) =>
        boundary.key === b[index]?.key &&
        rectEquals(boundary.rect, b[index]?.rect ?? null),
    )
  );
}

/**
 * The rendered cells a boundary's drag paints, across every copy of the
 * layout - the on-screen pages AND the renderer's inert off-screen measurer. A
 * table column's cells - its header and each body cell - carry its node id; an
 * fp-columns column is its row instance's child at the boundary's position.
 * The measurer copy is painted on purpose: pagination slices pages from the
 * measurer's heights, so a drag that reflows content (a narrower description
 * wraps taller) must reflow the measurer too, or the visible copies grow past
 * their frozen page slices and clip out of view. The on-screen copies come
 * first, since the drag reads its geometry off the first entry.
 * @param container - The scrollable preview container
 * @param target - The boundary's commit target
 * @returns The pair's rendered cells, on-screen copies first
 */
function boundaryCells(
  container: HTMLElement,
  target: BoundaryTarget,
): { cells: HTMLElement[]; neighbourCells: HTMLElement[] } {
  if (target.kind === "table") {
    return {
      cells: tableColumnCells(container, target.nodeId),
      neighbourCells: tableColumnCells(container, target.neighbourNodeId),
    };
  }
  const rows = allBlockCopies(container, target.nodeId);
  const columnAt = (index: number): HTMLElement[] =>
    rows
      .map((row) => row.children[index])
      .filter((cell): cell is HTMLElement => cell instanceof HTMLElement);
  return {
    cells: columnAt(target.index),
    neighbourCells: columnAt(target.index + 1),
  };
}

/**
 * Every rendered copy of a block: its on-screen instances first, then the
 * inert measurer copies pagination measures from.
 * @param container - The scrollable preview container
 * @param nodeId - The block's positional node id
 * @returns The block's rendered copies, on-screen ones first
 */
function allBlockCopies(container: HTMLElement, nodeId: string): HTMLElement[] {
  const onScreen = findRenderedBlockInstances(container, nodeId);
  const inert = Array.from(
    container.querySelectorAll(
      `[data-fp-node="${nodeId}"], [${INSTANCE_OF_ATTRIBUTE}="${nodeId}"]`,
    ),
  ).filter(
    (element): element is HTMLElement =>
      element instanceof HTMLElement && isRenderArtifact(element),
  );
  return [...onScreen, ...inert];
}

/**
 * A table column's rendered cells plus, per rendered copy, the `<colgroup>`
 * `<col>` at its position. The table's fixed layout sizes its columns from the
 * column group, not from the cells, so a drag that painted only the cells
 * would not move the table until the commit re-rendered it. The `<col>` is
 * found through one cell per table copy whose row holds a cell per grid
 * column, since only there does a cell's index name its column - a spanning
 * or break-shortened row misaligns. The anchor cell stays first, since the
 * drag reads the column's declared width and rendered width off the first
 * entry.
 * @param container - The scrollable preview container
 * @param nodeId - The column's node id
 * @returns The column's cells followed by its `<col>` elements
 */
function tableColumnCells(
  container: HTMLElement,
  nodeId: string,
): HTMLElement[] {
  const cells = allBlockCopies(container, nodeId);
  const resolvedTables = new Set<Element>();
  const cols = cells.flatMap((cell) => {
    if (!(cell instanceof HTMLTableCellElement)) {
      return [];
    }
    const table = cell.closest("table");
    if (table === null || resolvedTables.has(table)) {
      return [];
    }
    const columns = table.querySelectorAll(":scope > colgroup > col");
    if (cell.parentElement?.children.length !== columns.length) {
      return [];
    }
    resolvedTables.add(table);
    const col = columns[cell.cellIndex];
    return col instanceof HTMLElement ? [col] : [];
  });
  return [...cells, ...cols];
}

/**
 * A rendered cell's declared percentage width, read from the one channel its
 * layout actually uses: `width` for a table cell, `flex-basis` for an
 * fp-columns column. Reading the other channel would trust a value the layout
 * ignores - an fp-cell's authored `width` under a flex row, say - and the drag
 * preview would then disagree with what the commit renders.
 * @param cell - The rendered cell, or undefined when absent
 * @param kind - Which layout the cell sits in
 * @returns The percentage, or null when the channel declares none
 */
function percentageWidthOf(
  cell: HTMLElement | undefined,
  kind: BoundaryTarget["kind"],
): number | null {
  const declared =
    kind === "table"
      ? (cell?.style.width ?? "")
      : (cell?.style.flexBasis ?? "");
  const value = declared.endsWith("%")
    ? Number.parseFloat(declared)
    : Number.NaN;
  return Number.isFinite(value) ? value : null;
}

/**
 * Writes a width straight onto a column's rendered cells, so the layout follows
 * the pointer without the document being reparsed on every move. Only the
 * channel the layout reads is written; painting the other one would leave a
 * stale value behind that the commit's re-render never clears.
 * @param cells - The column's rendered cells
 * @param percent - The width to paint
 * @param kind - Which layout the cells sit in
 * @returns Nothing; the cells are mutated in place
 */
function paintWidth(
  cells: HTMLElement[],
  percent: number,
  kind: BoundaryTarget["kind"],
): void {
  cells.forEach((cell) => {
    if (kind === "table") {
      cell.style.width = `${percent.toFixed(2)}%`;
      return;
    }
    cell.style.flexBasis = `${percent.toFixed(2)}%`;
  });
}
